<template>
	<div class="dropdown">
		<button
			class="btn btn-sm btn-secondary dropdown-toggle"
			type="button"
			@click="open = !open"
			data-bs-toggle="dropdown"
			:id="'dropdown-' + componentUUID"
		>
			Filters ({{ totalFilters }})
		</button>

		<div
			class="dropdown-menu"
			:aria-labelledby="'dropdown-' + componentUUID"
			@click.stop=""
			style="width: 35rem"
		>
			<div
				v-for="(filter, index) in filters"
				class="d-flex flex-row gap-2 p-2"
				:key="index"
			>
				<div
					class="
						form-check form-switch
						d-flex
						align-items-center
						justify-content-center
					"
				>
					<input
						class="form-check-input"
						type="checkbox"
						:checked="filter.enabled"
						title="Enabled"
						@change="
							$emit('toggle', { index, enabled: !filter.enabled })
						"
					/>
				</div>

				<input
					class="form-control"
					type="text"
					readonly
					:value="filter.title"
				/>
				<input
					class="form-control text-center"
					type="text"
					readonly
					:value="filter.operator"
					style="max-width: 3rem"
				/>
				<input
					class="form-control"
					type="text"
					readonly
					:value="filter.compareTo"
				/>

				<button
					class="btn btn-outline-secondary"
					@click="$emit('delete', index)"
					title="delete"
				>
					<i class="bi bi-trash-fill" />
				</button>
			</div>

			<form
				class="d-flex flex-row gap-2 p-2"
				@submit.prevent="handleAddFilter"
			>
				<select
					class="form-select"
					style="width: auto"
					v-model="newFilter.key"
					@change="onKeyChange"
				>
					<option
						v-for="option in options"
						:value="option.value"
						:key="option.value"
					>
						{{ option.title }}
					</option>
				</select>

				<select
					class="form-select"
					style="width: auto"
					v-model="newFilter.operator"
				>
					<option
						v-for="option in comparisonOptions"
						:value="option"
						:key="option"
					>
						{{ option }}
					</option>
				</select>

				<input
					v-model="newFilter.compareTo"
					type="text"
					class="form-control"
				/>

				<button type="submit" class="btn btn-sm btn-outline-secondary">
					Add
				</button>
			</form>
		</div>
	</div>
</template>

<script lang="ts">
import { ArrayFilter, ComparisonOperator } from "@/hooks/useArrayFilters";
import { useUUID } from "@/hooks/useUUID";
import { computed, defineComponent, ref, toRefs } from "vue";

export type NewArrayFilter = Pick<
	ArrayFilter,
	"key" | "operator" | "compareTo"
> & { title?: string; enabled?: boolean };

export default defineComponent({
	props: {
		options: {
			type: Array as () => {
				value: string;
				title: string;
				comparisonOperators?: ComparisonOperator[];
			}[],
			required: true,
		},
		filters: {
			type: Array as () => (ArrayFilter & { title: string })[],
			required: true,
		},
	},
	setup(props, { emit }) {
		const { options, filters } = toRefs(props);

		const getComparisonOptionsForKey = (key: string) => {
			const item = options.value.find(option => option.value === key);
			if (item && item.comparisonOperators) {
				return item.comparisonOperators;
			}
			return Object.values(ComparisonOperator);
		};

		const initialFilter: NewArrayFilter = {
			key: options.value[0].value,
			operator: getComparisonOptionsForKey(options.value[0].value)[0],
			compareTo: "",
		};

		const newFilter = ref<NewArrayFilter>({ ...initialFilter });
		const comparisonOptions = computed(() =>
			getComparisonOptionsForKey(newFilter.value.key)
		);
		const onKeyChange = () => {
			const key = newFilter.value.key;
			const operators = getComparisonOptionsForKey(key);
			if (!operators.includes(newFilter.value.operator)) {
				newFilter.value.operator = getComparisonOptionsForKey(key)[0];
			}
		};
		const handleAddFilter = () => {
			const title = options.value.find(
				option => option.value === newFilter.value.key
			)?.title;
			if (title) {
				emit("new-filter", { ...newFilter.value, title });
				newFilter.value = { ...initialFilter };
			}
		};

		const totalFilters = computed(
			() => filters.value.filter(filter => filter.enabled).length
		);

		const componentUUID = useUUID();

		return {
			newFilter,
			comparisonOptions,
			onKeyChange,
			handleAddFilter,
			totalFilters,
			componentUUID,
		};
	},
});
</script>
